import { Slice } from "prosemirror-model"
import { Plugin, PluginKey } from "prosemirror-state"
import { EditorView } from "prosemirror-view"

import { parseEditorNodeData } from "@/models/EditorData"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { accountModule } from "@/store/modules/account.module"

// Stable identity used to dedupe files reported by multiple clipboard channels.
const getFileSignature = (file: File) => `${file.name}:${file.size}:${file.type}`

const getFileNameFromUrl = (url: string) => {
  try {
    const { pathname } = new URL(url)
    const name = pathname.split("/").pop()
    return name || "pasted-image"
  } catch {
    return "pasted-image"
  }
}

const isSafeImageSource = (src: string) => {
  if (!src) return false
  if (src.startsWith("data:") || src.startsWith("blob:")) return true

  try {
    const parsedUrl = new URL(src, window.location.origin)
    const isHttp = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:"
    if (!isHttp) return false
    return parsedUrl.origin === window.location.origin
  } catch {
    return false
  }
}

// Converts pasted <img src="..."> nodes to File objects so they can be uploaded
// through the same callback as regular clipboard files.
const getFilesFromImageNodes = async (doc: Document): Promise<File[]> => {
  const images = Array.from(doc.querySelectorAll("img"))
  const imageSources = images
    .map(image => image.getAttribute("src"))
    .filter((src): src is string => !!src)
    .filter(isSafeImageSource)

  if (!imageSources.length) return []

  const fileResults = await Promise.all(
    imageSources.map(async src => {
      try {
        const response = await fetch(src)
        if (!response.ok) return null
        const blob = await response.blob()
        if (!blob.size) return null
        if (!blob.type || !blob.type.startsWith("image/")) return null

        const mimeType = blob.type || "image/png"
        const extension = mimeType.split("/")[1] || "png"
        const fallbackName = `${getFileNameFromUrl(src)}.${extension}`
        const name = getFileNameFromUrl(src).includes(".") ? getFileNameFromUrl(src) : fallbackName

        return new File([blob], name, { type: mimeType })
      } catch {
        return null
      }
    })
  )

  return fileResults.filter((file): file is File => !!file)
}

// Clipboard HTML can encode JSON with escaped quotes, so we support both forms.
const parseJsonAttr = (value: string | null) => {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch {
    if (!value.includes("&quot;")) return null
  }

  try {
    return JSON.parse(value.replace(/&quot;/g, '"'))
  } catch {
    return null
  }
}

const getFilesFromReferenceNodes = async (doc: Document): Promise<File[]> => {
  // Pasted content from editor often serializes files as <reference configtype="FILE"> nodes.
  const references = Array.from(doc.querySelectorAll("reference")).filter(
    reference => (reference.getAttribute("configtype") || "").toUpperCase() === "FILE"
  )
  if (!references.length) return []

  const accountId = accountModule.accountId
  // We need current account to resolve attachment endpoint for file blob fetch.
  if (!accountId) return []

  const fileResults = await Promise.all(
    references.map(async reference => {
      const infoFromInfoAttr = parseJsonAttr(reference.getAttribute("info"))
      const dataAttr = reference.getAttribute("data")
      const infoFromDataAttr = (() => {
        if (!dataAttr) return null
        const attrs = parseEditorNodeData(dataAttr, true)
        return attrs?.info ? parseJsonAttr(attrs.info) : null
      })()

      const fileId = infoFromInfoAttr?.id || infoFromDataAttr?.id
      const fileName =
        infoFromInfoAttr?.name ||
        infoFromDataAttr?.name ||
        reference.getAttribute("title") ||
        "pasted-file"
      if (!fileId) return null

      try {
        // Fetch original stored attachment and re-wrap it as File for upload callback.
        const blobResponse = await apiService.getBlob<Blob>(
          Endpoint.ATTACHMENT_BY_ACCOUNT(accountId, fileId)
        )
        if (!(blobResponse instanceof Blob) || !blobResponse.size) return null
        const blob = blobResponse
        return new File([blob], fileName, { type: blob.type || "application/octet-stream" })
      } catch {
        return null
      }
    })
  )

  return fileResults.filter((file): file is File => !!file)
}

const getClipboardItemFiles = (items: DataTransferItem[]): File[] =>
  items
    .filter(item => item.kind === "file")
    .map(item => item.getAsFile())
    .filter((file): file is File => !!file)

const emitUniqueFiles = (
  files: File[],
  callBack: (file: File) => void,
  emittedSignatures: Set<string>
) => {
  // Clipboard may expose the same file via multiple sources (files/items/html).
  files.forEach(file => {
    const signature = getFileSignature(file)
    if (emittedSignatures.has(signature)) return
    emittedSignatures.add(signature)
    try {
      callBack(file)
    } catch (error) {
      console.error("Failed to emit pasted file:", error)
    }
  })
}

interface TransferData {
  files: File[]
  itemFiles: File[]
  html: string
  shouldHandleAsAttachment: boolean
}

const extractTransferData = (dataTransfer: DataTransfer | null): TransferData => {
  const items = Array.from(dataTransfer?.items || [])
  const files = Array.from(dataTransfer?.files || [])
  const itemFiles = getClipboardItemFiles(items)
  const html = dataTransfer?.getData("text/html") || ""
  const hasReferenceFileInHtml = /<reference[^>]*configtype=["']FILE["']/i.test(html)
  const hasImageInHtml = /<img[^>]+src=["']([^"']+)["'][^>]*>/i.test(html)
  const shouldHandleAsAttachment =
    files.length > 0 || itemFiles.length > 0 || hasReferenceFileInHtml || hasImageInHtml
  return { files, itemFiles, html, shouldHandleAsAttachment }
}

const processTransferredFiles = (
  { files, itemFiles, html }: TransferData,
  callBack: (file: File) => void
) => {
  const emittedSignatures = new Set<string>()
  emitUniqueFiles([...files, ...itemFiles], callBack, emittedSignatures)

  if (html && typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html")
    void Promise.all([getFilesFromReferenceNodes(doc), getFilesFromImageNodes(doc)]).then(
      ([referenceFiles, htmlImageFiles]) => {
        emitUniqueFiles([...referenceFiles, ...htmlImageFiles], callBack, emittedSignatures)
      }
    )
  }
}

const removePastedFileNodes = (view: EditorView, slice: Slice) => {
  const { state } = view
  const from = state.selection.from
  const tr = state.tr.replaceSelection(slice)
  const to = tr.selection.from
  const rangesToDelete: Array<{ from: number; to: number }> = []

  // Remove only file-like nodes from the just-inserted range, keeping plain text/content.
  tr.doc.nodesBetween(from, to, (node, pos) => {
    const isAttachmentNode = node.type.name === "attachment"
    const isFileReferenceNode =
      node.type.name.toLowerCase().includes("reference") &&
      ["FILE", "FILE_LINK"].includes((node.attrs?.configType || "").toUpperCase())

    if (isAttachmentNode || isFileReferenceNode) {
      rangesToDelete.push({ from: pos, to: pos + node.nodeSize })
      // Intentional: once a file-like node is selected for deletion, traversing its children
      // is unnecessary because the entire node subtree is removed as one range.
      return false
    }
    return true
  })

  rangesToDelete
    // Delete from right to left to keep offsets valid while mutating the transaction.
    .sort((a, b) => b.from - a.from)
    .forEach(range => tr.delete(range.from, range.to))

  view.dispatch(tr)
}

export const pastePlugin = (callBack: (file: File) => void) =>
  new Plugin({
    key: new PluginKey("pasteFilePlugin"),
    props: {
      handlePaste(view: EditorView, event: ClipboardEvent, slice: Slice) {
        const transfer = extractTransferData(event.clipboardData)
        if (!transfer.shouldHandleAsAttachment) {
          // Allow ProseMirror to handle normal paste (keeps Link extension paste behavior).
          return false
        }

        event.preventDefault()
        // Keep pasted text/content in editor, but remove file/reference nodes from the inserted slice.
        removePastedFileNodes(view, slice)
        processTransferredFiles(transfer, callBack)
        return true
      },

      handleDrop(_, event) {
        const transfer = extractTransferData(event.dataTransfer)
        if (!transfer.shouldHandleAsAttachment) return false

        event.preventDefault()
        processTransferredFiles(transfer, callBack)
        return true
      }
    }
  })
